import React, { useEffect, useRef, useState } from 'react';

// Square Web Payments SDK Apple Pay button.
//
// Loads the Square Web Payments SDK on demand, builds a GBP payment request
// from the live total, and renders the native Apple Pay button on verified
// domains + Apple devices only. On click it calls tokenize() immediately (an
// Apple Pay requirement — no async work may run between the click and
// tokenize()), then hands the resulting Square token to the parent via
// onToken(token). The parent is responsible for any SCA verification
// (squareVerifyBuyer) and the backend charge.
//
// Hidden silently where Apple Pay is not available, so non-Apple devices and
// unverified domains fall back to the existing card checkout untouched.

const SQUARE_APP_ID = import.meta.env.VITE_SQUARE_APP_ID || 'sq0idp-HblpXluK_lgmGh2wdXzdCA';
const SQUARE_LOCATION_ID = import.meta.env.VITE_SQUARE_LOCATION_ID || 'LHHYN7QJW6P94';

export function loadSquareSdk() {
  return new Promise((resolve, reject) => {
    if (window.Square) return resolve(window.Square);
    const existing = document.querySelector('script[src*="web.squarecdn.com/v1/square.js"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Square), { once: true });
      existing.addEventListener('error', () => reject(new Error('Square SDK load error')), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.id = 'sq-sdk';
    s.src = 'https://web.squarecdn.com/v1/square.js';
    s.onload = () => resolve(window.Square);
    s.onerror = () => reject(new Error('Square SDK load error'));
    document.head.appendChild(s);
  });
}

// SCA / 3DS buyer verification for an Apple Pay token. Returns the
// verification token to pass alongside the source id to the Square charge.
export async function squareVerifyBuyer(token, verificationDetails) {
  const Square = await loadSquareSdk();
  const payments = Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
  const verification = await payments.verifyBuyer(token, verificationDetails);
  return verification.token;
}

export default function SquareApplePayButton({
  amount,
  label = 'Motion Reality',
  onToken,
  onError,
  validate,
  onAvailabilityChange,
  disabled,
  containerId = 'sq-applepay-btn',
}) {
  const [available, setAvailable] = useState(false);
  const instanceRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  const validateRef = useRef(validate);
  const onAvailRef = useRef(onAvailabilityChange);
  const disabledRef = useRef(disabled);
  onTokenRef.current = onToken;
  onErrorRef.current = onError;
  validateRef.current = validate;
  onAvailRef.current = onAvailabilityChange;
  disabledRef.current = disabled;

  useEffect(() => {
    let cancelled = false;
    const cleanup = () => {
      if (instanceRef.current) { try { instanceRef.current.destroy(); } catch {} instanceRef.current = null; }
    };
    if (!amount || amount <= 0) { setAvailable(false); cleanup(); return; }

    (async () => {
      try {
        await loadSquareSdk();
        if (cancelled) return;
        const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
        cleanup();
        const paymentRequest = payments.paymentRequest({
          countryCode: 'GB',
          currencyCode: 'GBP',
          total: { amount: Number(amount).toFixed(2), label },
        });
        const ap = await payments.applePay(paymentRequest);
        if (cancelled) { try { await ap.destroy(); } catch {} return; }
        const container = document.getElementById(containerId);
        if (!container) { try { await ap.destroy(); } catch {} return; }
        container.innerHTML = '';
        await ap.attach('#' + containerId);
        // tokenize() must be the first async call in the click handler.
        // A synchronous pre-check (validate) is permitted before it.
        container.onclick = async () => {
          if (disabledRef.current) return;
          if (typeof validateRef.current === 'function') {
            const err = validateRef.current();
            if (err) { if (onErrorRef.current) onErrorRef.current(err); return; }
          }
          try {
            const tokenResult = await ap.tokenize();
            if (tokenResult.status !== 'OK') {
              const msg = tokenResult.errors?.[0]?.message;
              if (msg && onErrorRef.current) onErrorRef.current(msg);
              return;
            }
            if (onTokenRef.current) onTokenRef.current(tokenResult.token);
          } catch {
            // Sheet dismissed / cancelled by the buyer — no error needed.
          }
        };
        instanceRef.current = ap;
        setAvailable(true);
        if (onAvailRef.current) onAvailRef.current(true);
      } catch {
        if (cancelled) return;
        instanceRef.current = null;
        setAvailable(false);
        if (onAvailRef.current) onAvailRef.current(false);
      }
    })();

    return () => { cancelled = true; cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, label, containerId]);

  return (
    <div style={{ display: available && !disabled ? 'block' : 'none' }}>
      <div id={containerId} className="min-h-[48px] [&_button]:w-full" />
    </div>
  );
}